import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import DatePicker from "react-datepicker";
import { Icons } from "../icons";

// Date-only values must stay in local time — parsing or serialising through
// UTC can shift the chosen day. Same helpers as the Resources invite form.
function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatLocalDate(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Invite a client into an organisation.
 *
 * The role is always "viewer" and is therefore not shown here — but it is
 * stored normally, so the client appears in Settings > Resources with its
 * role like any other account.
 */
const InviteClientModal = ({ open, onClose, onInvite, isSaving, error }) => {
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [localError, setLocalError] = useState("");

  if (!open) return null;

  const close = () => {
    setEmail("");
    setDescription("");
    setStartDate("");
    setLocalError("");
    onClose();
  };

  const submit = async () => {
    if (!email.trim()) {
      setLocalError("Email is required.");
      return;
    }
    if (!startDate) {
      setLocalError("Start date is required.");
      return;
    }
    setLocalError("");
    const ok = await onInvite({
      email: email.trim(),
      description: description.trim(),
      dateOfJoining: startDate,
    });
    if (ok) {
      setEmail("");
      setDescription("");
      setStartDate("");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-50" onClick={close} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[600px] max-w-[calc(100vw-32px)] p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-[20px] font-semibold text-black">
              Invite Client
            </h2>
            <p className="text-[13px] text-gray-500 mt-1">
              Invite a client contact to view this organisation's work.
            </p>
          </div>
          <button onClick={close} aria-label="Close">
            <X size={24} className="text-black opacity-50" />
          </button>
        </div>

        <hr className="my-5" />

        {(localError || error) && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
            {localError || error}
          </div>
        )}

        {/* Email */}
        <div className="mb-4">
          <label className="text-sm font-medium">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@company.com"
            className="w-full h-[42px] mt-1 border border-gray-300 rounded-lg px-4 text-sm outline-none"
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="text-sm font-medium">Description</label>
          <input
            type="text"
            value={description}
            maxLength={100}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Primary contact for billing"
            className="w-full h-[42px] mt-1 border border-gray-300 rounded-lg px-4 text-sm outline-none"
          />
          <p className="text-[11px] text-gray-400 mt-1 text-right">
            {description.length}/100
          </p>
        </div>

        {/* Start Date */}
        <div className="mb-6">
          <label className="text-sm font-medium">Start Date</label>
          <div className="relative mt-1">
            <div className="absolute left-0 top-0 h-[42px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 z-10">
              <Icons.Calendar />
            </div>
            <DatePicker
              wrapperClassName="w-full"
              selected={parseLocalDate(startDate)}
              onChange={(date) =>
                setStartDate(date ? formatLocalDate(date) : "")
              }
              placeholderText="Select start date"
              dateFormat="dd MMM yyyy"
              className="w-full h-[42px] border border-gray-300 rounded-lg pl-[55px] pr-3 text-sm outline-none"
            />
          </div>
        </div>

        <div className="mb-6 p-3 bg-[#F0F0FF] rounded-lg">
          <p className="text-[12px] text-[#4253C7]">
            An invitation will be sent to this email to complete their account
            setup.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={close}
            disabled={isSaving}
            className="border border-[#4253C7] text-[#4253C7] w-[140px] h-[40px] rounded-lg text-sm disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={isSaving}
            className="bg-[#4253C7] text-white w-[140px] h-[40px] rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isSaving && <Loader2 size={14} className="animate-spin" />}
            Invite
          </button>
        </div>
      </div>
    </>
  );
};

export default InviteClientModal;
